import type { OrderFeedEntry, OrderFeedResult, OrderModel } from '@hopdrive/feature-order-index';

const isUpcomingPhase = (model: OrderModel): boolean => model.phase === 'planned' || model.phase === 'queued';

/**
 * Guest Rides has no scheduled time, and a ride waiting to be called or retried is action-needed,
 * so every unsettled ride lives in Active Orders and none in Up next.
 */
const isGuestRide = (model: OrderModel): boolean => model.product === 'guest-rides';

/** The Active Orders zone: in-motion orders, plus the guest rides still upcoming. */
export const isActiveOrder = (model: OrderModel): boolean => model.phase === 'active' || (isGuestRide(model) && isUpcomingPhase(model));

/** The Up next zone. */
export const isUpNextOrder = (model: OrderModel): boolean => isUpcomingPhase(model) && !isGuestRide(model);

const anchorOf = (entry: OrderFeedEntry): number =>
  entry.kind === 'order' ? entry.model.anchorMs ?? Number.POSITIVE_INFINITY : Number.POSITIVE_INFINITY;

/**
 * The feed narrowed to one zone's orders, sorted by scheduled time and windowed to one page. It reads
 * `pagedEntries`, the page-hydrated orders. `entries` is index depth, so slicing that would leave
 * every card stuck as a skeleton.
 */
export const sliceHomeFeed = (
  feed: OrderFeedResult,
  matches: (model: OrderModel) => boolean,
  page = 0,
  pageSize: number = Number.MAX_SAFE_INTEGER,
): OrderFeedResult => {
  const matching: OrderFeedEntry[] = feed.pagedEntries
    .filter(entry => entry.kind === 'order' && matches(entry.model))
    .sort((a, b) => anchorOf(a) - anchorOf(b));
  const pageCount = Math.max(1, Math.ceil(matching.length / pageSize));
  const safePage = Math.min(Math.max(page, 0), pageCount - 1);
  const start = safePage * pageSize;
  return {
    ...feed,
    entries: matching,
    pagedEntries: matching.slice(start, start + pageSize),
    pageSections: null,
    metricCounts: {},
    page: safePage,
    pageCount,
    totalCount: matching.length,
  };
};
